import { MEDIA_TYPE_LABELS, MediaType, OkResponse, type Product } from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Modal } from '../../components/Overlays';
import { ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { formatBytes } from '../../lib/backups-api';
import { MEDIA_ACCEPT, useInvalidateMedia, useProductMedia } from '../../lib/media-api';
import { errorMessage } from '../../lib/messages';
import { MediaDetail, MediaTile } from '../media/MediaDetail';
import { statusText, useMediaQueue } from '../media/upload-queue';
import { Combobox } from '../../components/Combobox';
import { productVariantOption } from '../../components/pickers';

const UploadIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 16V4M7 9l5-5 5 5M4 20h16" />
  </svg>
);

function UploadDialog({
  product,
  variantId: initialVariant,
  initialFiles,
  onClose,
}: {
  product: Product;
  variantId: string | null;
  initialFiles: File[] | null;
  onClose: (stillUploading: boolean) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const refresh = useInvalidateMedia();
  const [variantId, setVariantId] = useState(initialVariant);
  const [type, setType] = useState<MediaType>('product');
  const [over, setOver] = useState(false);
  const queue = useMediaQueue({ productId: product.id, variantId }, refresh);
  const started = useRef(false);
  const { add } = queue;
  useEffect(() => {
    // Files dropped on the grid's "Solte aqui" tile start right away (once, also in StrictMode).
    if (initialFiles && !started.current) {
      started.current = true;
      add(initialFiles, 'product');
    }
  }, [initialFiles, add]);
  const busy = queue.active > 0;

  return (
    <Modal
      title="Enviar fotos e vídeos"
      wide
      onClose={() => onClose(busy)}
      footer={
        <>
          {busy ? (
            <span className="xs muted grow">
              Pode fechar: os envios continuam enquanto esta página estiver aberta.
            </span>
          ) : null}
          <button type="button" className="btn p" onClick={() => onClose(busy)}>
            {queue.items.length === 0 ? 'Cancelar' : 'Concluir'}
          </button>
        </>
      }
    >
      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}
      >
        <div className="field">
          <label htmlFor="uv">Variação</label>
          <Combobox
            id="uv"
            value={variantId ?? ''}
            disabled={queue.items.length > 0}
            options={[
              { value: '', label: 'Todas (produto)', pinned: 'top' },
              ...product.variants.map(productVariantOption),
            ]}
            onChange={(v) => setVariantId(v || null)}
          />
        </div>
        <div className="field">
          <label htmlFor="ut">Tipo</label>
          <select
            id="ut"
            className="input"
            value={type}
            onChange={(e) => setType(MediaType.parse(e.target.value))}
          >
            {MediaType.options.map((t) => (
              <option key={t} value={t}>
                {MEDIA_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          <span className="help">Vídeos entram como Timelapse; dá para trocar em cada item.</span>
        </div>
      </div>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        accept={MEDIA_ACCEPT}
        onChange={(e) => {
          queue.add(e.target.files, type);
          e.target.value = '';
        }}
      />
      <div
        className={over ? 'dropzone over' : 'dropzone'}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          queue.add(e.dataTransfer.files, type);
        }}
      >
        <UploadIcon size={30} />
        <p style={{ fontWeight: 500, color: 'var(--ink)' }}>Arraste fotos e vídeos para cá</p>
        <p className="xs">
          JPG, PNG, WebP, HEIC, MP4, MOV · HEIC vira JPG · vídeos ganham prévia comprimida
        </p>
        <button type="button" className="btn sm" onClick={() => input.current?.click()}>
          Escolher arquivos
        </button>
      </div>
      {queue.items.map((i) => {
        const pct = i.total ? Math.round((i.sent / i.total) * 100) : 0;
        const uploading =
          i.status === 'uploading' || i.status === 'offline' || i.status === 'paused';
        return (
          <div key={i.key} className="row" style={{ gap: 10, alignItems: 'center' }}>
            {i.preview ? (
              <img
                src={i.preview}
                alt=""
                style={{
                  width: 44,
                  height: 44,
                  objectFit: 'cover',
                  borderRadius: 8,
                  flex: '0 0 auto',
                }}
              />
            ) : (
              <span
                className="ph"
                style={{ width: 44, height: 44, fontSize: 10, flex: '0 0 auto', padding: 2 }}
              >
                {i.file.type.startsWith('video/') ? 'vídeo' : 'foto'}
              </span>
            )}
            <div className="grow col" style={{ gap: 4, minWidth: 0 }}>
              <span className="row between" style={{ gap: 8 }}>
                <span className="trunc sm" title={i.file.name}>
                  {i.file.name}
                </span>
                <span className="xs muted" style={{ flex: '0 0 auto' }}>
                  {formatBytes(i.file.size)}
                </span>
              </span>
              {uploading ? (
                <div className="layers thin">
                  <div className="fill" style={{ width: `${pct}%` }} />
                  <div className="nozzle" style={{ left: `${pct}%` }} />
                </div>
              ) : null}
              <span
                className="xs"
                style={{
                  color:
                    i.status === 'error'
                      ? 'var(--err)'
                      : i.status === 'offline'
                        ? 'var(--warn)'
                        : i.status === 'added'
                          ? 'var(--ok)'
                          : 'var(--ink2)',
                }}
              >
                {statusText(i)}
              </span>
            </div>
            <select
              className="input"
              aria-label={`Tipo de ${i.file.name}`}
              style={{ height: 34, width: 'auto', flex: '0 0 auto' }}
              value={i.type}
              disabled={i.status === 'added' || i.status === 'done'}
              onChange={(e) => queue.setType(i.key, MediaType.parse(e.target.value))}
            >
              {MediaType.options.map((t) => (
                <option key={t} value={t}>
                  {MEDIA_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            {uploading ? (
              <button
                type="button"
                className="btn sm g"
                onClick={() => (i.status === 'paused' ? i.handle?.resume() : i.handle?.pause())}
              >
                {i.status === 'paused' ? 'Retomar' : 'Pausar'}
              </button>
            ) : null}
          </div>
        );
      })}
    </Modal>
  );
}

/** "Mídia" tab (board Produto-Visao, Mídia). */
export function MediaTab({ product }: { product: Product }) {
  const media = useProductMedia(product.id);
  const toast = useToast();
  const refresh = useInvalidateMedia();
  const [variant, setVariant] = useState('');
  const [type, setType] = useState<MediaType | ''>('');
  const [selId, setSelId] = useState<string | null>(null);
  const [upload, setUpload] = useState<{ variantId: string | null; files: File[] | null } | null>(
    null,
  );
  const [dragId, setDragId] = useState<string | null>(null);
  const move = useMutation({
    mutationFn: (v: { id: string; position: number }) =>
      api.post(`/media/${v.id}/move`, OkResponse, { position: v.position }),
    onSuccess: refresh,
    onError: (err) => toast(errorMessage(err)),
  });

  if (media.isPending) return <Skeleton rows={3} />;
  if (media.isError)
    return (
      <ErrorBox title="Não foi possível carregar as mídias" onRetry={() => void media.refetch()} />
    );

  const all = media.data.data;
  const list = all.filter(
    (m) => (!variant || m.variant?.id === variant) && (!type || m.type === type),
  );
  const sel = all.find((m) => m.id === selId) ?? null;
  const varName = product.variants.find((v) => v.id === variant)?.name ?? '';
  const varEmpty = !!variant && !all.some((m) => m.variant?.id === variant);
  const unfiltered = !variant && !type;
  const segs = [
    { id: '', label: 'Todas', n: all.length },
    ...product.variants.map((v) => ({
      id: v.id,
      label: v.size || v.name,
      n: all.filter((m) => m.variant?.id === v.id).length,
    })),
  ];
  const tagOf = (m: (typeof all)[number]) =>
    `${m.isProductCover ? 'Capa · ' : ''}${MEDIA_TYPE_LABELS[m.type]}${m.variant ? ` · ${product.variants.find((v) => v.id === m.variant!.id)?.size || m.variant.name}` : ''}`;

  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="row wrap" style={{ gap: 10 }}>
        {product.variants.length > 0 ? (
          <div className="seg" role="radiogroup" aria-label="Variação">
            {segs.map((s) => (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={variant === s.id}
                className={variant === s.id ? 'on' : ''}
                onClick={() => setVariant(s.id)}
              >
                {s.label} <span className="tab">{s.n}</span>
              </button>
            ))}
          </div>
        ) : null}
        <select
          className="input"
          aria-label="Tipo"
          style={{ width: 'auto' }}
          value={type}
          onChange={(e) => setType(e.target.value ? MediaType.parse(e.target.value) : '')}
        >
          <option value="">Todos os tipos</option>
          {MediaType.options.map((t) => (
            <option key={t} value={t}>
              {MEDIA_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <div className="grow" />
        <span className="sm muted">
          {unfiltered ? '↑ ↓ ou arrastar reordenam' : '↑ ↓ reordenam'} · a estrela marca a capa ·{' '}
          {formatBytes(media.data.totals.bytes)}
        </span>
        <button
          type="button"
          className="btn p"
          onClick={() => setUpload({ variantId: variant || null, files: null })}
        >
          <UploadIcon />
          Enviar fotos e vídeos
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-start' }}>
        <div
          style={{
            flex: '3 1 520px',
            minWidth: 0,
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))',
          }}
        >
          {list.map((m) => (
            <MediaTile
              key={m.id}
              item={m}
              aspect="4/5"
              selected={selId === m.id}
              onPick={() => setSelId(m.id)}
              tag={tagOf(m)}
              drag={
                unfiltered
                  ? {
                      onDragStart: () => setDragId(m.id),
                      onDragOver: (e) => {
                        if (dragId) e.preventDefault();
                      },
                      onDrop: () => {
                        if (dragId && dragId !== m.id) {
                          move.mutate({
                            id: dragId,
                            position: all.findIndex((x) => x.id === m.id),
                          });
                        }
                        setDragId(null);
                      },
                    }
                  : undefined
              }
            />
          ))}
          {all.length > 0 && !varEmpty ? (
            <button
              type="button"
              className="thumb"
              onClick={() => setUpload({ variantId: variant || null, files: null })}
              onDragOver={(e) => {
                if (!dragId) e.preventDefault();
              }}
              onDrop={(e) => {
                if (dragId) return;
                e.preventDefault();
                setUpload({ variantId: variant || null, files: Array.from(e.dataTransfer.files) });
              }}
              style={{
                aspectRatio: '4/5',
                border: '2px dashed var(--line)',
                background: 'transparent',
                flexDirection: 'column',
                gap: 8,
                padding: 12,
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              <UploadIcon size={26} />
              <span className="sm">Solte aqui</span>
              <span className="xs">JPG, PNG, WebP, HEIC, MP4, MOV</span>
            </button>
          ) : null}
          {varEmpty ? (
            <div
              className="card sunk col"
              style={{ gridColumn: '1/-1', gap: 8, padding: 16, alignItems: 'flex-start' }}
            >
              <p style={{ fontWeight: 500 }}>Variação {varName} ainda não tem fotos</p>
              <p className="sm muted">Sem capa própria, ela usa a capa do produto nos anúncios.</p>
              <button
                type="button"
                className="btn sm"
                onClick={() => setUpload({ variantId: variant, files: null })}
              >
                Enviar fotos da {varName}
              </button>
            </div>
          ) : null}
          {!varEmpty && all.length > 0 && list.length === 0 ? (
            <div
              className="card sunk row wrap between"
              style={{ gridColumn: '1/-1', gap: 8, padding: 16 }}
            >
              <div>
                <p style={{ fontWeight: 500 }}>Nada desse tipo nesta variação</p>
                <p className="sm muted">Troque o tipo ou veja todas as variações.</p>
              </div>
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setType('');
                  setVariant('');
                }}
              >
                Limpar filtros
              </button>
            </div>
          ) : null}
          {all.length === 0 ? (
            <div
              className="card sunk col"
              style={{ gridColumn: '1/-1', gap: 8, padding: 16, alignItems: 'flex-start' }}
            >
              <p style={{ fontWeight: 500 }}>Nenhuma foto ou vídeo ainda</p>
              <p className="sm muted">
                Pelo celular é mais rápido: tire a foto e marque o tipo na hora.
              </p>
              <div className="row wrap" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="btn sm p"
                  onClick={() => setUpload({ variantId: null, files: null })}
                >
                  Enviar fotos e vídeos
                </button>
                <Link className="btn sm" to={`/midia/enviar?produto=${product.id}`}>
                  Enviar pelo celular
                </Link>
              </div>
            </div>
          ) : null}
        </div>
        <aside
          className="card col"
          aria-label="Detalhes da mídia"
          style={{ flex: '1 1 300px', minWidth: 0, gap: 12, position: 'sticky', top: 16 }}
        >
          {sel ? (
            <MediaDetail
              item={sel}
              position={all.indexOf(sel) + 1}
              total={all.length}
              variants={product.variants}
              onClose={() => setSelId(null)}
            />
          ) : (
            <div className="col" style={{ gap: 6 }}>
              <p style={{ fontWeight: 500 }}>Nenhum item selecionado</p>
              <p className="sm muted">
                Toque numa foto ou vídeo para ver detalhes, aprovar e registrar onde foi postado.
              </p>
            </div>
          )}
        </aside>
      </div>
      {upload ? (
        <UploadDialog
          product={product}
          variantId={upload.variantId}
          initialFiles={upload.files}
          onClose={(stillUploading) => {
            setUpload(null);
            if (stillUploading) toast('Os envios continuam enquanto esta página estiver aberta');
          }}
        />
      ) : null}
    </div>
  );
}
